import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ProductBundleApiService } from '../services/product-bundle-api.service';
import { ProductBundleResponse, ProductBundleSearchParams } from '../models/product-bundle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ProductBundleDetailModalComponent } from '../product-bundle-detail-modal/product-bundle-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ProductoApiService } from '../services/producto-api.service';
import { CurrencyResponse } from '../models/producto.model';
import { currencyFractionDigits } from '../../../shared/utils/currency-format.utils';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-product-bundle-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ProductBundleDetailModalComponent
  ],
  templateUrl: './product-bundle-list.component.html'
})
export class ProductBundleListComponent implements OnInit {
  bundles: ProductBundleResponse[] = [];
  loading = false;
  error = '';
  monedas: CurrencyResponse[] = [];

  // Modal
  showDetailModal = false;
  selectedBundle: ProductBundleResponse | null = null;

  // Filters
  showFilters = false;
  filtroNombre = '';
  filtroSku = '';
  filtroIsActive: boolean | undefined;

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'bundle-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private bundleApi: ProductBundleApiService,
    private authService: AuthService,
    private productApi: ProductoApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarMonedas();
    this.cargarBundles();
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => this.monedas = data,
      error: () => {}
    });
  }

  getCurrencyCode(currencyId?: number): string {
    if (!currencyId) return 'USD';
    return this.monedas.find(m => m.id === currencyId)?.code || 'USD';
  }

  cargarBundles(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: ProductBundleSearchParams = {
      businessId,
      searchName: this.filtroNombre || undefined,
      searchSku: this.filtroSku || undefined,
      isActive: this.filtroIsActive,
      page: this.globalPage,
      size: this.pageSize
    };

    this.bundleApi.searchProductBundle(params).subscribe({
      next: (response) => {
        this.bundles = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar paquetes';
        this.loading = false;
      }
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarBundles();
  }

  limpiarFiltro(): void {
    this.filtroNombre = '';
    this.filtroSku = '';
    this.filtroIsActive = undefined;
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarBundles();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  nuevoBundle(): void {
    this.router.navigate(['/bundles/crear']);
  }

  editarBundle(bundle: ProductBundleResponse): void {
    this.router.navigate(['/bundles/editar', bundle.id]);
  }

  async eliminarBundle(bundle: ProductBundleResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar paquete',
      message: `¿Está seguro de eliminar el paquete "${bundle.name}"?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.bundleApi.deleteProductBundle(bundle.id, businessId).subscribe({
      next: () => {
        this.cargarBundles();
        this.toast.deleted('Paquete');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el paquete' });
      }
    });
  }

  verDetalle(bundle: ProductBundleResponse): void {
    this.selectedBundle = bundle;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedBundle = null;
  }

  getActiveColor(bundle: ProductBundleResponse): BadgeColor {
    return bundle.isActive ? 'success' : 'error';
  }

  getActiveLabel(bundle: ProductBundleResponse): string {
    return bundle.isActive ? 'Activo' : 'Inactivo';
  }

  /**
   * ¿Se debe mostrar el precio promocional en lugar del precio del paquete? Solo
   * cuando la promoción está VIGENTE, ENCENDIDA y además tiene precio: la bandera
   * `promoVigente` solo mira las fechas, así que una promoción a medias tachaba
   * el precio real y dejaba un "-" como precio actual (mismo criterio que en
   * Productos).
   */
  tienePromoActiva(bundle: ProductBundleResponse): boolean {
    return !!(
      bundle.promoVigente &&
      bundle.promoEnabled !== false &&
      bundle.promoPrice !== undefined &&
      bundle.promoPrice !== null
    );
  }

  formatPrecio(price?: number, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    const code = currencyCode || 'USD';
    try {
      // Sin minimumFractionDigits: Intl ya usa la unidad mínima ISO 4217 de la
      // moneda (0 en JPY, 2 en USD, 3 en KWD).
      return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: code
      }).format(price);
    } catch {
      return `${code} ${price.toFixed(currencyFractionDigits(code))}`;
    }
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarBundles();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] { return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1); }
  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }
}
